import { useState, useMemo } from 'react';
import { Paper, Modal, Stack, Text, Group, Button, TextInput } from '@mantine/core';
import { IconFilter, IconPlus, IconSearch } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useGetAllTranslationsQuery, useUpdateTranslationMutation } from '@codezo/graphql';
import { DataContainer, ScrollContainer } from '@codezo/ui';
import { TranslationsList } from '../components/TranslationsList';
import { TranslationUpdateForm } from '../components/TranslationUpdateForm';
import { TranslationCreateForm } from '../components/TranslationCreateForm';
import { TreeFilterModal } from '../components/TreeFilterModal';
import { buildTranslationTree, filterKeysByPath } from '../utils/translationTree';

export function TranslationsPage() {
	const [selectedTranslation, setSelectedTranslation] = useState<any | null>(null);
	const [updateModalOpened, setUpdateModalOpened] = useState(false);
	const [createModalOpened, setCreateModalOpened] = useState(false);
	const [filterModalOpened, setFilterModalOpened] = useState(false);
	const [selectedPath, setSelectedPath] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [sortBy, setSortBy] = useState<string>('key');
	const [sortOrder, setSortOrder] = useState<string>('asc');

	// Fetch translations for English (we'll update both languages in the form)
	const { data, loading, refetch } = useGetAllTranslationsQuery({
		variables: {
			language: 'en',
			search: searchQuery || undefined,
			sortBy,
			sortOrder,
		},
	});
	const [updateTranslation, { loading: updating }] = useUpdateTranslationMutation();

	const handleEdit = (translation: any) => {
		setSelectedTranslation(translation);
		setUpdateModalOpened(true);
	};

	const handleUpdateSubmit = async (values: any) => {
		try {
			await updateTranslation({
				variables: {
					input: values,
				},
			});

			notifications.show({
				title: 'Success',
				message: 'Translation updated successfully',
				color: 'green',
			});

			setUpdateModalOpened(false);
			setSelectedTranslation(null);
			refetch();
		} catch (error: any) {
			notifications.show({
				title: 'Error',
				message: error.message || 'Failed to update translation',
				color: 'red',
			});
		}
	};

	const handleCreateSubmit = async (values: any) => {
		try {
			await updateTranslation({
				variables: {
					input: values,
				},
			});

			notifications.show({
				title: 'Success',
				message: 'Translation created successfully',
				color: 'green',
			});

			setCreateModalOpened(false);
			refetch();
		} catch (error: any) {
			notifications.show({
				title: 'Error',
				message: error.message || 'Failed to create translation',
				color: 'red',
			});
		}
	};

	const handleCancel = () => {
		setUpdateModalOpened(false);
		setSelectedTranslation(null);
	};

	const translations = data?.getAllTranslations || [];

	// Build tree structure from translation keys
	const tree = useMemo(() => {
		const keys = translations.map((t) => t.key);
		return buildTranslationTree(keys);
	}, [translations]);

	// Filter translations based on selected path (client-side for tree navigation)
	const filteredTranslations = useMemo(() => {
		if (!selectedPath) return translations;
		const keys = translations.map((t) => t.key);
		const filteredKeys = filterKeysByPath(keys, selectedPath);
		return translations.filter((t) => filteredKeys.includes(t.key));
	}, [translations, selectedPath]);

	const handleSortChange = (field: string, direction: string) => {
		setSortBy(field);
		setSortOrder(direction);
	};

	return (
		<ScrollContainer>
			<Stack gap="lg">
				<Group justify="space-between">
					<div>
						<Text size="lg" fw={600}>
							App Translations
						</Text>
						<Text size="sm" c="dimmed">
							Manage application translations for multiple languages
						</Text>
					</div>
					<Group>
						<TextInput
							placeholder="Search translations..."
							leftSection={<IconSearch size={16} />}
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.currentTarget.value)}
							style={{ width: 300 }}
						/>
						<Button
							leftSection={<IconFilter size={18} />}
							variant={selectedPath ? 'filled' : 'light'}
							onClick={() => setFilterModalOpened(true)}
						>
							{selectedPath ? `Filter: ${selectedPath}` : 'Filter by Key'}
						</Button>
						<Button
							leftSection={<IconPlus size={18} />}
							onClick={() => setCreateModalOpened(true)}
						>
							New Translation
						</Button>
					</Group>
				</Group>

				<DataContainer loading={loading} error={undefined}>
					<Paper p="lg" withBorder style={{ overflow: 'hidden' }}>
						<TranslationsList
							translations={filteredTranslations}
							onEdit={handleEdit}
							onSortChange={handleSortChange}
							currentSortBy={sortBy}
							currentSortOrder={sortOrder}
						/>
					</Paper>
				</DataContainer>
			</Stack>

			<Modal
				opened={updateModalOpened}
				onClose={handleCancel}
				title="Update Translation"
				size="lg"
			>
				{selectedTranslation && (
					<TranslationUpdateForm
						initialValues={selectedTranslation}
						onSubmit={handleUpdateSubmit}
						onCancel={handleCancel}
						loading={updating}
					/>
				)}
			</Modal>

			<Modal
				opened={createModalOpened}
				onClose={() => setCreateModalOpened(false)}
				title="New Translation"
				size="lg"
			>
				<TranslationCreateForm
					onSubmit={handleCreateSubmit}
					onCancel={() => setCreateModalOpened(false)}
					loading={updating}
				/>
			</Modal>

			<TreeFilterModal
				opened={filterModalOpened}
				onClose={() => setFilterModalOpened(false)}
				tree={tree}
				selectedPath={selectedPath}
				onSelect={setSelectedPath}
			/>
		</ScrollContainer>
	);
}
